import { logClientError, logClientInformation } from "@/core/api/graphql";
import { Logger } from "@/core/utilities";
import { useUser } from "@/shared/account";
import type { PayerAuthenticationSetupResultType } from "@/core/api/graphql/types";

const DDC_IFRAME_ID = "cardinal_collection_iframe";
const DDC_FORM_ID = "cardinal_collection_form";
const DDC_TIMEOUT_MS = 10_000;

interface DeviceDataCollectionResult {
  success: boolean;
  sessionId?: string;
}

// Module-level promise tracking for DDC completion.
// This allows other parts of the application (e.g., order creation) to wait
// for DDC to complete before proceeding with the enrollment check.
let ddcPromise: Promise<DeviceDataCollectionResult> | null = null;

/**
 * Composable for CyberSource Payer Authentication Device Data Collection (DDC).
 *
 * After receiving the PA Setup response, DDC must be performed by posting
 * the accessToken (JWT) to the deviceDataCollectionUrl in a hidden iframe.
 * An event listener captures the completion callback from Cardinal Commerce.
 *
 * @see https://developer.cybersource.com/docs/cybs/en-us/payer-authentication/developer/all/rest/payer-auth/pa2-ccdc-ddc-intro.html
 */
export const useDeviceDataCollection = () => {
  const { user } = useUser();
  const memberId = () => user.value?.memberId ?? user.value?.id ?? "unknown";

  const cleanup = () => {
    const existingIframe = document.getElementById(DDC_IFRAME_ID);
    if (existingIframe) {
      existingIframe.remove();
    }
    const existingForm = document.getElementById(DDC_FORM_ID);
    if (existingForm) {
      existingForm.remove();
    }
  };

  /**
   * Extracts the origin from a URL string for use in event.origin validation.
   */
  const getOriginFromUrl = (url: string): string => {
    try {
      const parsedUrl = new URL(url);
      return parsedUrl.origin;
    } catch {
      Logger.error("useDeviceDataCollection", `Invalid deviceDataCollectionUrl: ${url}`);
      void logClientError({
        errorTitle: "useDeviceDataCollection",
        errorContent: `Invalid deviceDataCollectionUrl: ${url}`,
        stackTrace: "No stack trace available",
        userId: memberId(),
      }).catch(() => {
        // Logging failure must not affect the DDC flow
      });
      return "";
    }
  };

  /**
   * Performs Device Data Collection by:
   * 1. Creating a hidden iframe
   * 2. Creating a form that POSTs the JWT (accessToken) to the deviceDataCollectionUrl
   * 3. Listening for a message event from Cardinal Commerce indicating completion
   *
   * @param setupResult - The result from the createAuthenticationSetup call
   * @returns Promise that resolves with the DDC result (success/failure)
   */
  const performDeviceDataCollection = (
    setupResult: PayerAuthenticationSetupResultType,
  ): Promise<DeviceDataCollectionResult> => {
    const promise = new Promise<DeviceDataCollectionResult>((resolve) => {
      if (!setupResult.isSuccess) {
        Logger.error("useDeviceDataCollection", `Setup was not successful: ${setupResult.errorMessage}`);
        void logClientError({
          errorTitle: "useDeviceDataCollection",
          errorContent: `Setup was not successful: ${setupResult.errorMessage}`,
          stackTrace: "No stack trace available",
          userId: memberId(),
        }).catch(() => {
          // Logging failure must not affect the DDC flow
        });
        resolve({ success: false });
        return;
      }

      const { accessToken, deviceDataCollectionUrl } = setupResult;

      if (!accessToken || !deviceDataCollectionUrl) {
        Logger.error("useDeviceDataCollection", "Missing accessToken or deviceDataCollectionUrl");
        void logClientError({
          errorTitle: "useDeviceDataCollection",
          errorContent: `Missing accessToken or deviceDataCollectionUrl`,
          stackTrace: "No stack trace available",
          userId: memberId(),
        }).catch(() => {
          // Logging failure must not affect the DDC flow
        });
        resolve({ success: false });
        return;
      }

      const expectedOrigin = getOriginFromUrl(deviceDataCollectionUrl);

      if (!expectedOrigin) {
        resolve({ success: false });
        return;
      }

      Logger.info("useDeviceDataCollection", `DDC starting, deviceDataCollectionUrl: ${deviceDataCollectionUrl}`);
      void logClientInformation({
        title: "useDeviceDataCollection",
        content: `DDC starting, deviceDataCollectionUrl: ${deviceDataCollectionUrl}`,
        withTelemetry: true,
        userId: memberId(),
      }).catch(() => {
        // Logging failure must not affect the DDC flow
      });

      // Clean up any previous DDC elements
      cleanup();

      // Create hidden iframe
      const iframe = document.createElement("iframe");
      iframe.id = DDC_IFRAME_ID;
      iframe.name = "collectionIframe";
      iframe.height = "10";
      iframe.width = "10";
      iframe.style.display = "none";
      document.body.appendChild(iframe);

      // Create form targeting the iframe
      const form = document.createElement("form");
      form.id = DDC_FORM_ID;
      form.method = "POST";
      form.target = "collectionIframe";
      form.action = deviceDataCollectionUrl;

      const input = document.createElement("input");
      input.id = "cardinal_collection_form_input";
      input.type = "hidden";
      input.name = "JWT";
      input.value = accessToken;
      form.appendChild(input);

      document.body.appendChild(form);

      // Set up timeout in case DDC doesn't complete
      let resolved = false;
      const timeoutId = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          Logger.warn("useDeviceDataCollection", "Device Data Collection timed out");
          void logClientInformation({
            title: "useDeviceDataCollection",
            content: `Device Data Collection timed out`,
            withTelemetry: true,
            userId: memberId(),
          }).catch(() => {
            // Logging failure must not affect the DDC flow
          });
          window.removeEventListener("message", messageHandler);
          cleanup();
          resolve({ success: false });
        }
      }, DDC_TIMEOUT_MS);

      // Listen for DDC completion event
      const messageHandler = (event: MessageEvent) => {
        if (event.origin !== expectedOrigin) {
          return;
        }

        if (resolved) {
          return;
        }

        resolved = true;
        clearTimeout(timeoutId);
        window.removeEventListener("message", messageHandler);

        let data = event.data;
        if (typeof data === "string") {
          try {
            data = JSON.parse(data);
          } catch {
            // data is not JSON, use as-is
          }
        }

        const isCompleted = data?.MessageType === "profile.completed" && data?.Status === true;
        const sessionId = data?.["SessionId"] ?? data?.["Session Id"];

        Logger.info("useDeviceDataCollection", `DDC ${isCompleted ? "completed" : "failed"}, SessionId: ${sessionId}`);
        void logClientInformation({
          title: "useDeviceDataCollection",
          content: `DDC ${isCompleted ? "completed" : "failed"}, SessionId: ${sessionId}`,
          withTelemetry: true,
          userId: memberId(),
        }).catch(() => {
          // Logging failure must not affect the DDC flow
        });

        cleanup();
        resolve({
          success: isCompleted,
          sessionId,
        });
      };

      window.addEventListener("message", messageHandler, false);

      // Submit the form to trigger DDC
      form.submit();
    });

    ddcPromise = promise.finally(() => {
      ddcPromise = null;
    });
    return promise;
  };

  /**
   * Waits for the in-progress Device Data Collection to complete, up to 10 seconds.
   * If no DDC is in progress, resolves immediately.
   * If DDC does not complete within 10 seconds, proceeds anyway so order creation is not blocked.
   */
  const waitForDeviceDataCollection = async (): Promise<void> => {
    if (ddcPromise) {
      try {
        await Promise.race([ddcPromise, new Promise<void>((resolve) => setTimeout(resolve, DDC_TIMEOUT_MS))]);
      } catch {
        // DDC failure should not prevent order creation
      }
    }
  };

  return {
    performDeviceDataCollection,
    waitForDeviceDataCollection,
    cleanup,
  };
};
